import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Input, Modal, Row, Select, Space, Spin, Tooltip, Typography, message } from 'antd';
import StandardTable from '../../components/common/StandardTable';
import ExcelExportButton from '../../components/common/DownloadAsExcelFile';
import { fetchSqlData, fetchSqlDataWithQuery, updateDataBatch, deleteData } from '../../services/api';
import { useAuth } from '../../components/common/security/AuthContext';
import FilePreviewModal, { canPreviewFile } from '../../components/common/FilePreviewModal';
import type { DccTranslationPackage, DccTranslationPackageItemEnrichedRow } from '../../types';
import Button from '../../components/common/Button';

const { Text } = Typography;

const normalizeStr = (v: any) => String(v ?? '').trim();

const basename = (p: any): string => {
  const s = normalizeStr(p).replace(/\\/g, '/');
  if (!s) return '';
  const parts = s.split('/').filter(Boolean);
  return parts[parts.length - 1] || s;
};

const formatDateTime = (v: any): string => {
  const s = normalizeStr(v);
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
};

const unwrapRows = <T,>(resp: any): T[] => {
  if (Array.isArray(resp)) return resp as T[];
  if (Array.isArray(resp?.data)) return resp.data as T[];
  if (Array.isArray(resp?.data?.data)) return resp.data.data as T[];
  return [];
};

const normalizeNumericId = (v: any): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const s = normalizeStr(v);
  if (!s) return null;
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return n;
};

const DccTranslationPackages: React.FC = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [items, setItems] = useState<DccTranslationPackageItemEnrichedRow[]>([]);
  const [packages, setPackages] = useState<DccTranslationPackage[]>([]);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [createdByFilter, setCreatedByFilter] = useState<string | undefined>(undefined);
  const [vendorFilter, setVendorFilter] = useState<string | undefined>(undefined);
  const [packageIdFilter, setPackageIdFilter] = useState<string | undefined>(undefined);

  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [revoking, setRevoking] = useState(false);
  const [vendorValue, setVendorValue] = useState('');
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [newVendorModalOpen, setNewVendorModalOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [selectedItemIdsByPackageId, setSelectedItemIdsByPackageId] = useState<Record<string, number[]>>({});
  const [expandedPackageIds, setExpandedPackageIds] = useState<string[]>([]);

  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const recheckSinglePackage = async () => {
    const ids = (selectedPackageIds || []).map((v) => normalizeStr(v)).filter(Boolean);
    if (ids.length !== 1) {
      message.info('Select exactly one package to re-check');
      return;
    }
    const pid = ids[0];
    try {
      setLoading(true);

      const itemsForPkg = (itemsByPackageId[pid] || []) as any[];
      const primIds = itemsForPkg
        .filter((it) => String((it as any)?.role || '').toLowerCase() === 'primary')
        .map((it) => (it as any)?.translation_log_id)
        .filter((v) => typeof v === 'number' && Number.isFinite(v));

      if (!primIds.length) {
        message.info(`${pid}: no primary items with log links`);
        return;
      }

      const q = `SELECT id, status FROM dcc_translation_log WHERE id IN (${Array.from(new Set(primIds)).join(',')})`;
      const resp = await fetchSqlDataWithQuery(q);
      const rows: Array<{ id: number; status: string }> = unwrapRows(resp);
      const statusById = new Map<number, string>();
      (rows || []).forEach((r) => statusById.set(Number((r as any)?.id), normalizeStr((r as any)?.status).toLowerCase()));

      const missing = primIds.filter((id) => !statusById.has(id));
      if (missing.length) {
        message.info(`${pid}: ${missing.length} missing log link(s)`);
        return;
      }
      const notCompleted = primIds.filter((id) => statusById.get(id) !== 'completed');
      if (notCompleted.length) {
        message.info(`${pid}: ${notCompleted.length} primary item(s) not completed`);
        return;
      }

      const username = localStorage.getItem('user') || null;
      await updateDataBatch('dcc_translation_packages', {
        id_field: 'package_id',
        ids: [pid],
        data: { status: 'returned' },
      });
      message.success(`Package ${pid} promoted to returned`);
      await reload();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Re-check failed');
    } finally {
      setLoading(false);
    }
  };

  const revokeSelectedItemsForSinglePackage = async () => {
    const selectedPkgIds = (selectedPackageIds || []).map((v) => normalizeStr(v)).filter(Boolean);
    const pkgIdsWithSelectedItems = Object.keys(selectedItemIdsByPackageId || {}).filter((pid) => {
      const ids = (selectedItemIdsByPackageId[pid] || []).filter((v) => typeof v === 'number' && Number.isFinite(v));
      return ids.length > 0;
    });

    let pid = '';
    if (selectedPkgIds.length === 1) {
      pid = selectedPkgIds[0];
    } else if (pkgIdsWithSelectedItems.length === 1) {
      pid = normalizeStr(pkgIdsWithSelectedItems[0]);
    } else {
      message.info('Select items from exactly one package');
      return;
    }

    const itemIds = (selectedItemIdsByPackageId[pid] || []).filter((v) => typeof v === 'number' && Number.isFinite(v));
    if (!itemIds.length) {
      message.info('Select at least one item in the expanded package view');
      return;
    }

    const selectedItems = (itemsByPackageId[pid] || []).filter((it: any) => itemIds.includes(Number((it as any)?.id)));
    const logIds = Array.from(
      new Set(
        (selectedItems || [])
          .map((it: any) => normalizeNumericId((it as any)?.translation_log_id))
          .filter((v): v is number => typeof v === 'number')
      )
    );

    if (logIds.length) {
      try {
        const q = `SELECT id, status FROM dcc_translation_log WHERE id IN (${logIds.join(',')})`;
        const resp = await fetchSqlDataWithQuery(q);
        const rows: Array<{ id: number; status: string }> = unwrapRows(resp);
        const statusById = new Map<number, string>();
        (rows || []).forEach((r) => {
          const id = normalizeNumericId((r as any)?.id);
          if (typeof id === 'number') statusById.set(id, normalizeStr((r as any)?.status).toLowerCase());
        });

        const completed = logIds.filter((id) => statusById.get(id) === 'completed');
        if (completed.length) {
          message.warning(`Cannot revoke: ${completed.length} selected item(s) are already completed`);
          return;
        }
      } catch (e: any) {
        message.error(e?.response?.data?.error || e?.message || 'Failed to validate translation log statuses');
        return;
      }
    }

    Modal.confirm({
      title: 'Revoke selected item(s)',
      content: `This will remove ${itemIds.length} item(s) from package '${pid}' and put the related log item(s) back into the queue. Continue?`,
      okText: 'Revoke items',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setRevoking(true);

          // Delete selected package items
          for (const id of itemIds) {
            await deleteData('dcc_translation_package_items', 'id', id);
          }

          // Maintain counts in package header (frontend-driven)
          const remaining = (itemsByPackageId[pid] || []).filter((it: any) => !itemIds.includes(Number((it as any)?.id)));
          const primCount = remaining.filter((it: any) => normalizeStr((it as any)?.role).toLowerCase() === 'primary').length;
          const supCount = remaining.filter((it: any) => normalizeStr((it as any)?.role).toLowerCase() === 'supporting').length;
          await updateDataBatch('dcc_translation_packages', {
            id_field: 'package_id',
            ids: [pid],
            data: { file_count_primary: primCount, file_count_supporting: supCount },
          });

          setSelectedItemIdsByPackageId((prev) => {
            const next = { ...prev };
            delete next[pid];
            return next;
          });

          message.success(`Revoked ${itemIds.length} item(s) from ${pid}`);
          await reload();
        } catch (e: any) {
          message.error(e?.response?.data?.error || e?.message || 'Failed to revoke item(s)');
        } finally {
          setRevoking(false);
        }
      },
    });
  };
  const reload = async () => {
    try {
      setLoading(true);
      setError(null);

      const [pkgData, itemData] = await Promise.all([
        fetchSqlData('dcc_translation_packages'),
        fetchSqlData('vw_dcc_translation_package_items_enriched'),
      ]);

      const pkgArr: DccTranslationPackage[] = unwrapRows<DccTranslationPackage>(pkgData);
      const itemArr: DccTranslationPackageItemEnrichedRow[] = unwrapRows<DccTranslationPackageItemEnrichedRow>(itemData);

      setPackages(pkgArr || []);
      setItems(itemArr || []);
    } catch (e: any) {
      setPackages([]);
      setItems([]);
      setError(e?.response?.data?.error || e?.message || 'Failed to load translation packages');
    } finally {
      setLoading(false);
    }
  };

  const revokeSelectedPackageSends = async () => {
    const pkgIds = (selectedPackageIds || []).map((v) => normalizeStr(v)).filter(Boolean);
    if (!pkgIds.length) {
      message.info('Select at least one package');
      return;
    }

    if (!allSelectedSentBySender) {
      message.warning('Only sent packages can be revoked, and only by the sender');
      return;
    }

    const logIdsRaw = pkgIds
      .flatMap((pid) => (itemsByPackageId[pid] || []))
      .map((it: any) => it?.translation_log_id);
    const logIds = Array.from(new Set(logIdsRaw.map(normalizeNumericId).filter((v): v is number => typeof v === 'number')));

    if (!logIds.length) {
      message.warning('Selected package(s) have no translation log records to validate');
      return;
    }

    try {
      setRevoking(true);

      // Rule: can only revoke send if ALL related log rows are currently sent
      const q = `SELECT id, status FROM dcc_translation_log WHERE id IN (${logIds.join(',')})`;
      const resp = await fetchSqlDataWithQuery(q);
      const rows: Array<{ id: number; status: string }> = unwrapRows(resp);

      const statusById = new Map<number, string>();
      (rows || []).forEach((r) => {
        const id = normalizeNumericId((r as any)?.id);
        if (typeof id === 'number') statusById.set(id, normalizeStr((r as any)?.status).toLowerCase());
      });

      const missingIds = logIds.filter((id) => !statusById.has(id));
      if (missingIds.length) {
        message.error(`Cannot validate ${missingIds.length} translation log record(s); aborting`);
        return;
      }

      const notSent = logIds.filter((id) => (statusById.get(id) || '') !== 'sent');
      if (notSent.length) {
        message.warning('Cannot revoke send: at least one file is not in sent status (e.g., completed)');
        return;
      }

      Modal.confirm({
        title: 'Revoke send',
        content: `This will set ${pkgIds.length} package(s) back to status 'draft', clear vendor, and set related translation log records back to 'packed' (also clearing vendor). Continue?`,
        okText: 'Revoke send',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            const username = localStorage.getItem('user') || null;
            const updateMeta: any = {
              status: 'draft',
              sent_date: null,
              sender: null,
              vendor: null,
            };
            

            await updateDataBatch('dcc_translation_packages', { id_field: 'package_id', ids: pkgIds, data: updateMeta });

            await updateDataBatch('dcc_translation_log', { id_field: 'id', ids: logIds, data: { status: 'packed', vendor: null } });

            message.success(`Revoked send for ${pkgIds.length} package(s)`);
            setSelectedPackageIds([]);
            await reload();
          } catch (e: any) {
            message.error(e?.response?.data?.error || e?.message || 'Failed to revoke send');
          }
        },
      });
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Failed to validate translation log statuses');
    } finally {
      setRevoking(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemsByPackageId = useMemo(() => {
    const out: Record<string, DccTranslationPackageItemEnrichedRow[]> = {};
    (items || []).forEach((it) => {
      const pid = normalizeStr((it as any)?.package_id);
      if (!pid) return;
      if (!out[pid]) out[pid] = [];
      out[pid].push(it);
    });
    return out;
  }, [items]);

  const packageIdOptions = useMemo(() => {
    const ids = (packages || [])
      .map((p) => normalizeStr((p as any)?.package_id))
      .filter(Boolean);
    ids.sort((a, b) => a.localeCompare(b));
    return ids.map((id) => ({ value: id, label: id }));
  }, [packages]);

  const vendorOptions = useMemo(() => {
    const uniq = new Set<string>();
    (packages || []).forEach((p) => {
      const v = normalizeStr((p as any)?.vendor);
      if (v) uniq.add(v);
    });
    return Array.from(uniq)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
  }, [packages]);

  const vendorOptionsWithCurrent = useMemo(() => {
    const opts = vendorOptions.slice();
    const cur = normalizeStr(vendorValue);
    if (cur && !opts.some((o) => normalizeStr(o.value) === cur)) {
      opts.unshift({ value: cur, label: cur });
    }
    return opts;
  }, [vendorOptions, vendorValue]);

  const createdByOptions = useMemo(() => {
    const uniq = new Set<string>();
    (packages || []).forEach((p) => {
      const v = normalizeStr((p as any)?.created_by);
      if (v) uniq.add(v);
    });
    return Array.from(uniq)
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
  }, [packages]);

  const filteredPackages = useMemo(() => {
    let out = packages || [];

    if (statusFilter) {
      out = out.filter((p) => normalizeStr((p as any)?.status).toLowerCase() === normalizeStr(statusFilter).toLowerCase());
    }
    if (createdByFilter) {
      out = out.filter((p) => normalizeStr((p as any)?.created_by).toLowerCase() === normalizeStr(createdByFilter).toLowerCase());
    }
    if (vendorFilter) {
      out = out.filter((p) => normalizeStr((p as any)?.vendor).toLowerCase() === normalizeStr(vendorFilter).toLowerCase());
    }
    if (packageIdFilter) {
      out = out.filter((p) => normalizeStr((p as any)?.package_id).toLowerCase() === normalizeStr(packageIdFilter).toLowerCase());
    }

    const q = normalizeStr(query).toLowerCase();
    if (q) {
      const terms = q.split(/\s+/).filter(Boolean);
      out = out.filter((p) => {
        const pid = normalizeStr((p as any)?.package_id);
        const hay = [
          (p as any)?.status,
          (p as any)?.vendor,
          (p as any)?.created_by,
          (p as any)?.translationpair,
          (p as any)?.sent_date,
          (p as any)?.sender,
          (p as any)?.notes,
          (p as any)?.file_count_primary,
          (p as any)?.file_count_supporting,
          (p as any)?.updated_at,
          (p as any)?.created_at,
        ]
          .map((v) => normalizeStr(v).toLowerCase())
          .join(' ');
        // Also allow searching on item filenames within the package
        const itemHay = (itemsByPackageId[pid] || [])
          .map((it) => basename((it as any)?.file_path))
          .map((v) => normalizeStr(v).toLowerCase())
          .join(' ');
        const combined = `${hay} ${itemHay}`.trim();
        return terms.every((t) => combined.includes(t));
      });
    }

    return out;
  }, [createdByFilter, itemsByPackageId, packageIdFilter, packages, query, statusFilter, vendorFilter]);

  const statusOptions = useMemo(() => {
    const uniq = new Set<string>();
    (packages || []).forEach((p) => {
      const st = normalizeStr((p as any)?.status);
      if (st) uniq.add(st);
    });
    return Array.from(uniq)
      .sort((a, b) => a.localeCompare(b))
      .map((st) => ({ value: st, label: st }));
  }, [packages]);

  const selectedPkById = useMemo(() => {
    const byId = new Map<string, DccTranslationPackage>();
    (packages || []).forEach((p) => {
      const pid = normalizeStr((p as any)?.package_id);
      if (pid) byId.set(pid, p);
    });
    const out = new Map<string, DccTranslationPackage>();
    (selectedPackageIds || []).forEach((pid) => {
      const p = byId.get(normalizeStr(pid));
      if (p) out.set(normalizeStr(pid), p);
    });
    return out;
  }, [packages, selectedPackageIds]);

  const allSelectedSentBySender = useMemo(() => {
    const arr = Array.from(selectedPkById.values());
    if (!arr.length) return false;
    const cu = normalizeStr(localStorage.getItem('user') || '').toLowerCase();
    return arr.every((p: any) => {
      const st = normalizeStr(p?.status).toLowerCase();
      const sender = normalizeStr(p?.sender).toLowerCase();
      return st === 'sent' && Boolean(sender) && sender === cu;
    });
  }, [selectedPkById]);

  const allSelectedDraft = useMemo(() => {
    const arr = Array.from(selectedPkById.values());
    if (!arr.length) return false;
    return arr.every((p) => normalizeStr((p as any)?.status).toLowerCase() === 'draft');
  }, [selectedPkById]);

  const assignSelectedPackages = async () => {
    const vendor = normalizeStr(vendorValue);
    const pkgIds = (selectedPackageIds || []).map((v) => normalizeStr(v)).filter(Boolean);
    if (!pkgIds.length) {
      message.info('Select at least one package');
      return;
    }
    if (!allSelectedDraft) {
      message.warning('Only draft packages can be assigned');
      return;
    }
    if (!vendor) {
      message.info('Enter/select a vendor');
      return;
    }

    Modal.confirm({
      title: 'Assign vendor',
      content: `Assign vendor '${vendor}' to ${pkgIds.length} package(s) and mark as sent?`,
      okText: 'Assign',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setAssigning(true);

          const username = localStorage.getItem('user') || null;
          const sentDate = new Date().toISOString().slice(0, 10);
          const updateMeta: any = {
            vendor,
            status: 'sent',
            sent_date: sentDate,
            sender: username || null,
          };
          

          await updateDataBatch('dcc_translation_packages', { id_field: 'package_id', ids: pkgIds, data: updateMeta });

          const logIds = pkgIds
            .flatMap((pid) => (itemsByPackageId[pid] || []))
            .map((it: any) => it?.translation_log_id)
            .filter((v: any) => typeof v === 'number' || (typeof v === 'string' && String(v).trim() !== ''));

          if (logIds.length) {
            await updateDataBatch('dcc_translation_log', {
              id_field: 'id',
              ids: Array.from(new Set(logIds)),
              data: { status: 'sent' },
            });
          }

          message.success(`Assigned vendor and marked ${pkgIds.length} package(s) as sent`);
          setVendorValue('');
          setSelectedPackageIds([]);
          await reload();
        } catch (e: any) {
          message.error(e?.response?.data?.error || e?.message || 'Failed to assign vendor');
        } finally {
          setAssigning(false);
        }
      },
    });
  };

  const revokeSelectedPackages = async () => {
    const pkgIds = (selectedPackageIds || []).map((v) => normalizeStr(v)).filter(Boolean);
    if (!pkgIds.length) {
      message.info('Select at least one package');
      return;
    }

    if (!allSelectedDraft) {
      message.warning('Only draft packages can be revoked');
      return;
    }

    const pkById = new Map<string, DccTranslationPackage>();
    (packages || []).forEach((p) => {
      const pid = normalizeStr((p as any)?.package_id);
      if (pid) pkById.set(pid, p);
    });

    const blocked = pkgIds.filter((id) => {
      const p = pkById.get(id) as any;
      const status = normalizeStr(p?.status).toLowerCase();
      const vendor = normalizeStr(p?.vendor);
      return status === 'sent' || Boolean(vendor);
    });

    if (blocked.length) {
      message.warning(`Cannot revoke ${blocked.length} package(s) that are sent/assigned to a vendor`);
      return;
    }

    Modal.confirm({
      title: 'Revoke package(s)',
      content: `This will delete ${pkgIds.length} package(s) and their items, and reset related translation log records back to queued. Continue?`,
      okText: 'Revoke',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setRevoking(true);

          // Reset translation log records first (best-effort)
          const logIds = pkgIds
            .flatMap((pid) => (itemsByPackageId[pid] || []))
            .map((it: any) => it?.translation_log_id)
            .filter((v: any) => typeof v === 'number' || (typeof v === 'string' && String(v).trim() !== ''));

          if (logIds.length) {
            await updateDataBatch('dcc_translation_log', {
              id_field: 'id',
              ids: Array.from(new Set(logIds)),
              data: { status: 'queued' },
            });
          }

          // Delete headers (items are deleted via FK ON DELETE CASCADE)
          for (const pid of pkgIds) {
            await deleteData('dcc_translation_packages', 'package_id', pid);
          }

          message.success(`Revoked ${pkgIds.length} package(s)`);
          setSelectedPackageIds([]);
          await reload();
        } catch (e: any) {
          message.error(e?.response?.data?.error || e?.message || 'Failed to revoke package(s)');
        } finally {
          setRevoking(false);
        }
      },
    });
  };

  const columns: any[] = [
    {
      title: 'Package',
      dataIndex: 'package_id',
      key: 'package_id',
      width: 100,
      render: (v: any) => {
        const s = normalizeStr(v);
        if (!s) return '';
        return <Text code>{s}</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 60,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      key: 'vendor',
      width: 60,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Pair',
      dataIndex: 'translationpair',
      key: 'translationpair',
      width: 60,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Sent',
      dataIndex: 'sent_date',
      key: 'sent_date',
      width: 80,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Sender',
      dataIndex: 'sender',
      key: 'sender',
      width: 70,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Created by',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 80,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Folders',
      dataIndex: 'folder_count',
      key: 'folder_count',
      width: 60,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Primary',
      dataIndex: 'file_count_primary',
      key: 'file_count_primary',
      width: 60,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Supporting',
      dataIndex: 'file_count_supporting',
      key: 'file_count_supporting',
      width: 60,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'Last update',
      key: 'last_update',
      width: 100,
      render: (_: any, rec: any) => {
        const raw = (rec as any)?.updated_at ?? (rec as any)?.created_at;
        const txt = formatDateTime(raw);
        if (!txt) return '';
        return (
          <Tooltip title={normalizeStr(raw)} placement="topLeft">
            <span>{txt}</span>
          </Tooltip>
        );
      },
    },
  ];

  // Selective columns for the dedicated "package contents" Excel export only
  // (not used by StandardTable's general Export to Excel utility).
  const packageExportColumns = useMemo(
    () => [
      { key: 'package_id', title: 'Package' },
      { key: 'role', title: 'Role' },
      { key: 'file_name', title: 'File' },
      { key: 'page_count', title: 'Pages' },
      { key: 'char_count', title: 'Characters' },
      { key: 'sent_date', title: 'Sent' },
      { key: 'translationpair', title: 'Pair' },
    ],
    []
  );

  const packageExportData = useMemo(() => {
    const out: any[] = [];
    (filteredPackages || []).forEach((p: any) => {
      const pid = normalizeStr(p?.package_id);
      const sentDate = normalizeStr(p?.sent_date);
      const pair = normalizeStr(p?.translationpair);

      // Package header row (no file)
      out.push({
        package_id: pid,
        role: '',
        file_name: '',
        page_count: null,
        char_count: null,
        sent_date: sentDate,
        translationpair: pair,
      });

      (itemsByPackageId[pid] || []).forEach((it: any) => {
        const fp = normalizeStr(it?.file_path);
        out.push({
          package_id: pid,
          role: normalizeStr(it?.role),
          file_name: basename(fp),
          page_count: (it as any)?.page_count ?? null,
          char_count: (it as any)?.char_count ?? null,
          sent_date: sentDate,
          translationpair: pair,
        });
      });
    });
    return out;
  }, [filteredPackages, itemsByPackageId]);

  const selectedPackageExportData = useMemo(() => {
    const ids = new Set((selectedPackageIds || []).map((v) => normalizeStr(v)).filter(Boolean));
    if (!ids.size) return [];
    return (packageExportData || []).filter((r: any) => ids.has(normalizeStr(r?.package_id)));
  }, [packageExportData, selectedPackageIds]);

  const selectedExportFileName = useMemo(() => {
    const ids = (selectedPackageIds || []).map((v) => normalizeStr(v)).filter(Boolean);
    if (ids.length === 1) return `dcc_translation_package_${ids[0]}`;
    if (ids.length > 1) return `dcc_translation_packages_selected_${ids.length}`;
    return 'dcc_translation_packages_selected';
  }, [selectedPackageIds]);

  const itemColumns: any[] = [
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 60,
      render: (v: any) => normalizeStr(v),
    },
    {
      title: 'File',
      dataIndex: 'file_path',
      key: 'file_path',
      width: 520,
      render: (v: any) => {
        const s = normalizeStr(v);
        if (!s) return '';
        const name = basename(s);
        const previewable = canPreviewFile(s);
        return (
          <Tooltip title={s} placement="topLeft">
            {previewable ? (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto' }}
                onClick={() => {
                  setPreviewPath(s);
                  setIsPreviewOpen(true);
                }}
              >
                {name}
              </Button>
            ) : (
              <span>{name}</span>
            )}
          </Tooltip>
        );
      },
    },
    {
      title: 'Pages',
      dataIndex: 'page_count',
      key: 'page_count',
      width: 70,
      align: 'right',
      render: (v: any) => (
        <span style={{ display: 'inline-block', width: '100%', textAlign: 'right' }}>{v == null || v === '' ? '' : String(v)}</span>
      ),
    },
    {
      title: 'Chars',
      dataIndex: 'char_count',
      key: 'char_count',
      width: 80,
      align: 'right',
      render: (v: any) => (
        <span style={{ display: 'inline-block', width: '100%', textAlign: 'right' }}>{v == null || v === '' ? '' : String(v)}</span>
      ),
    },
    {
      title: 'Needs OCR',
      dataIndex: 'needs_ocr',
      key: 'needs_ocr',
      width: 90,
      align: 'center',
      render: (v: any) => {
        if (v === true)
          return (
            <span style={{ display: 'inline-block', width: '100%', textAlign: 'center' }}>
              <Text type="danger">Yes</Text>
            </span>
          );
        if (v === false)
          return (
            <span style={{ display: 'inline-block', width: '100%', textAlign: 'center' }}>
              <Text type="secondary">No</Text>
            </span>
          );
        return <span style={{ display: 'inline-block', width: '100%' }} />;
      },
    },
  ];

  return (
    <Card style={{ width: '100%', maxWidth: '100%' }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={4}>
            <Select
              allowClear
              showSearch
              placeholder="Package ID"
              value={packageIdFilter as any}
              onChange={(v) => setPackageIdFilter((v as any) || undefined)}
              options={packageIdOptions}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(String(input).toLowerCase())
              }
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Input
              allowClear
              placeholder="Search (vendor/user/file...)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Col>
          <Col xs={24} md={3}>
            <Select
              allowClear
              placeholder="Package status"
              value={statusFilter as any}
              onChange={(v) => setStatusFilter((v as any) || undefined)}
              options={statusOptions}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={3}>
            <Select
              allowClear
              showSearch
              placeholder="Vendor"
              value={vendorFilter as any}
              onChange={(v) => setVendorFilter((v as any) || undefined)}
              options={vendorOptions}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(String(input).toLowerCase())
              }
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={3}>
            <Select
              allowClear
              showSearch
              placeholder="Created by"
              value={createdByFilter as any}
              onChange={(v) => setCreatedByFilter((v as any) || undefined)}
              options={createdByOptions}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(String(input).toLowerCase())
              }
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} md={3}>
            <Button
              action="edit"
              type="primary"
              onClick={() => setVendorModalOpen(true)}
              loading={assigning}
              disabled={!allSelectedDraft || !selectedPackageIds.length}
              style={{ width: '100%' }}
            >
              Assign
            </Button>
          </Col>
          <Col span={24}>
            <Space size={8} wrap>
              <Button
                onClick={() => {
                  const keys = (filteredPackages || [])
                    .map((p: any) => normalizeStr((p as any)?.package_id))
                    .filter(Boolean);
                  setExpandedPackageIds(keys);
                }}
              >
                Expand all
              </Button>
              <Button onClick={() => setExpandedPackageIds([])}>Collapse all</Button>
              <div style={{ pointerEvents: selectedPackageIds.length ? 'auto' : 'none', opacity: selectedPackageIds.length ? 1 : 0.5 }}>
                <ExcelExportButton
                  data={() => selectedPackageExportData}
                  filename={`${selectedExportFileName}.xlsx`}
                  sheetName="Packages"
                  size="middle"
                  columns={packageExportColumns}
                />
              </div>
              <Button onClick={reload} loading={loading}>Reload</Button>
              <Button
                danger
                onClick={revokeSelectedPackages}
                loading={revoking}
                disabled={!allSelectedDraft || !selectedPackageIds.length}
              >
                Revoke package
              </Button>
              <Button
                danger
                onClick={revokeSelectedItemsForSinglePackage}
                loading={revoking}
                disabled={(() => {
                  const pkgIdsWithSelectedItems = Object.keys(selectedItemIdsByPackageId || {}).filter((pid) => (selectedItemIdsByPackageId[pid] || []).length > 0);
                  return !(((selectedPackageIds || []).length === 1) || (pkgIdsWithSelectedItems.length === 1));
                })()}
              >
                Revoke items
              </Button>
              <Button
                danger
                onClick={revokeSelectedPackageSends}
                loading={revoking}
                disabled={!allSelectedSentBySender || !selectedPackageIds.length}
              >
                Revoke send
              </Button>
              <Button
                onClick={recheckSinglePackage}
                disabled={(selectedPackageIds || []).length !== 1}
                loading={loading}
              >
                Re-check status
              </Button>
            </Space>
          </Col>
        </Row>

        {error && <Alert type="error" message="Failed to load packages" description={error} showIcon />}

        <Spin spinning={loading}>
          <StandardTable
            columns={columns}
            dataSource={filteredPackages}
            rowKey={(r: any) => String((r as any)?.package_id || (r as any)?.id)}
            rowSelection={{
              selectedRowKeys: selectedPackageIds as any,
              onChange: (keys) => setSelectedPackageIds((keys as any[]).map((k) => String(k))),
            }}
            size="small"
            style={{ width: '100%' }}
            scroll={{ x: 'max-content' }}
            expandable={{
              columnWidth: 32,
              indentSize: 32,
              expandedRowKeys: expandedPackageIds as any,
              onExpandedRowsChange: (keys: any) => setExpandedPackageIds((keys as any[]).map((k) => String(k))),
              expandedRowRender: (rec: any) => {
                const pid = normalizeStr(rec?.package_id);
                const allItems = (itemsByPackageId[pid] || []).slice();
                allItems.sort((a, b) => normalizeStr((a as any)?.role).localeCompare(normalizeStr((b as any)?.role)));

                const q = normalizeStr(query).toLowerCase();
                const terms = q ? q.split(/\s+/).filter(Boolean) : [];
                const data = terms.length
                  ? allItems.filter((it: any) => {
                      const fp = normalizeStr((it as any)?.file_path);
                      const hay = [basename(fp), fp, normalizeStr((it as any)?.role)]
                        .map((v) => normalizeStr(v).toLowerCase())
                        .join(' ');
                      return terms.every((t) => hay.includes(t));
                    })
                  : allItems;

                const computedPrimary = data.filter((r) => normalizeStr((r as any)?.role).toLowerCase() === 'primary').length;
                const computedSupporting = data.filter((r) => normalizeStr((r as any)?.role).toLowerCase() === 'supporting').length;
                const primaryCount = rec?.file_count_primary ?? computedPrimary;
                const supportingCount = rec?.file_count_supporting ?? computedSupporting;

                return (
                  <div
                    key={`${pid}::${normalizeStr(query)}`}
                    style={{ paddingLeft: 16, background: '#e6f4ff', borderRadius: 6, paddingTop: 8, paddingBottom: 8 }}
                  >
                    <div style={{ marginBottom: 6 }}>
                      <Text type="secondary">
                        Primary: <Text strong>{normalizeStr(primaryCount) || '0'}</Text>
                        {'  '}Supporting: <Text strong>{normalizeStr(supportingCount) || '0'}</Text>
                        {'  '}Pair: <Text strong>{normalizeStr((rec as any)?.translationpair) || '-'}</Text>
                        {'  '}Sent: <Text strong>{normalizeStr((rec as any)?.sent_date) || '-'}</Text>
                        {'  '}Sender: <Text strong>{normalizeStr((rec as any)?.sender) || '-'}</Text>
                      </Text>
                    </div>
                    <StandardTable
                      enableExport={false}
                      enableColumnTools={false}
                      enableResetView={false}
                      columns={itemColumns}
                      dataSource={data}
                      rowKey={(r: any) => String((r as any)?.id ?? `${pid}::${(r as any)?.file_path}`)}
                      rowSelection={{
                        selectedRowKeys: ((selectedItemIdsByPackageId[pid] || []).map((id) => String(id)) as any),
                        onChange: (keys) => {
                          const ids = (keys as any[])
                            .map((k) => Number(k))
                            .filter((v) => Number.isFinite(v)) as number[];
                          setSelectedItemIdsByPackageId((prev) => ({ ...prev, [pid]: ids }));
                        },
                      }}
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>
                );
              },
            }}
            pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100', '200'] }}
          />
        </Spin>
      </Space>

      <Modal
        title="Assign vendor"
        open={vendorModalOpen}
        onCancel={() => setVendorModalOpen(false)}
        onOk={async () => {
          await assignSelectedPackages();
          setVendorModalOpen(false);
        }}
        okText="Assign"
        confirmLoading={assigning}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            showSearch
            placeholder="Select a vendor"
            value={vendorValue || undefined}
            onChange={(v) => setVendorValue(String(v || ''))}
            options={vendorOptionsWithCurrent}
            filterOption={(input, option) =>
              String(option?.label || '').toLowerCase().includes(String(input || '').toLowerCase())
            }
            style={{ width: '100%' }}
          />
          <Button size="small" onClick={() => setNewVendorModalOpen(true)}>
            Create new vendor
          </Button>
        </Space>
      </Modal>

      <Modal
        title="Create new vendor"
        open={newVendorModalOpen}
        onCancel={() => {
          setNewVendorModalOpen(false);
          setNewVendorName('');
        }}
        onOk={() => {
          const nv = normalizeStr(newVendorName);
          if (!nv) {
            message.info('Enter a vendor name');
            return;
          }
          setVendorValue(nv);
          setNewVendorModalOpen(false);
          setNewVendorName('');
          message.success(`Vendor '${nv}' set`);
        }}
        okText="Add"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input
            placeholder="Vendor name"
            value={newVendorName}
            onChange={(e) => setNewVendorName(e.target.value)}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            This will set the vendor field to the entered name for assignment. It does not create a persisted vendor record.
          </Typography.Text>
        </Space>
      </Modal>

      <FilePreviewModal
        open={isPreviewOpen}
        path={previewPath}
        token={token}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewPath(null);
        }}
        width="85%"
      />
    </Card>
  );
};

export default DccTranslationPackages;
